import type { FunctionComponent } from 'react';
import classNames from 'classnames';

import { forTemplate } from '@trello/legacy-i18n';
import { currentLocale } from '@trello/locale';
import { BackIcon } from '@trello/nachos/icons/back';
import { ExternalLinkIcon } from '@trello/nachos/icons/external-link';
import { navigate } from '@trello/router/navigate';
import { RouterLink } from '@trello/router/router-link';

import { usePowerUpPageNavigationQuery } from './PowerUpPageNavigationQuery.generated';

import styles from './Navigation.less';
const format = forTemplate('app_management');

// eslint-disable-next-line @trello/assets-alongside-implementation
import defaultPowerUpIcon from 'resources/images/directory/icons/customIcon.png';
// eslint-disable-next-line @trello/assets-alongside-implementation
import defaultAppIcon from 'resources/images/directory/icons/keyIcon.png';

export const Navigation: FunctionComponent = () => {
  return (
    <div className={styles.sidebar}>
      <div className={styles.navigation}>
        <div className={styles.navigationSection}>
          <div className={styles.navigationSectionHeading}>
            {format('apps')}
          </div>
          <ul>
            <li>
              <RouterLink
                className={classNames(styles.navigationLink, styles.isActive)}
                href="/power-ups/admin"
              >
                {format('power-ups')}
              </RouterLink>
            </li>
          </ul>
        </div>

        <div className={styles.navigationSection}>
          <div className={styles.navigationSectionHeading}>
            {format('getting-started')}
          </div>
          <ul>
            <li>
              <a
                className={styles.navigationLink}
                href="https://developers.trello.com/docs/your-first-power-up"
                target="_blank"
                rel="noopener noreferrer"
              >
                {format('your-first-power-up')}
                <ExternalLinkIcon />
              </a>
            </li>
            <li>
              <a
                className={styles.navigationLink}
                href="https://developers.trello.com/docs/power-up-guidelines"
                target="_blank"
                rel="noopener noreferrer"
              >
                {format('power-up-guidelines')}
                <ExternalLinkIcon />
              </a>
            </li>
            <li>
              <a
                className={styles.navigationLink}
                href="https://developers.trello.com/docs/rate-limits"
                target="_blank"
                rel="noopener noreferrer"
              >
                {format('rate-limits')}
                <ExternalLinkIcon />
              </a>
            </li>
          </ul>
        </div>

        <div className={styles.navigationSection}>
          <div className={styles.navigationSectionHeading}>
            {format('reference')}
          </div>
          <ul>
            <li>
              <a
                className={styles.navigationLink}
                href="https://developers.trello.com/reference#introduction"
                target="_blank"
                rel="noopener noreferrer"
              >
                {format('rest-api')}
                <ExternalLinkIcon />
              </a>
            </li>
            <li>
              <a
                className={styles.navigationLink}
                href="https://developers.trello.com/reference#power-ups-intro"
                target="_blank"
                rel="noopener noreferrer"
              >
                {format('power-ups')}
                <ExternalLinkIcon />
              </a>
            </li>
            <li>
              <a
                className={styles.navigationLink}
                href="https://developer.atlassian.com/cloud/trello/changelog/"
                target="_blank"
                rel="noopener noreferrer"
              >
                {format('changelog')}
                <ExternalLinkIcon />
              </a>
            </li>
            <li>
              <a
                className={styles.navigationLink}
                href="https://developer.atlassian.com/platform/marketplace/atlassian-developer-terms/"
                target="_blank"
                rel="noopener noreferrer"
              >
                {format('developer-terms')}
                <ExternalLinkIcon />
              </a>
            </li>
          </ul>
        </div>

        <div className={styles.navigationSection}>
          <div className={styles.navigationSectionHeading}>
            {format('community')}
          </div>
          <ul>
            <li>
              <a
                className={styles.navigationLink}
                href="https://developers.trello.com/docs/community-projects"
                target="_blank"
                rel="noopener noreferrer"
              >
                {format('community-projects')}
                <ExternalLinkIcon />
              </a>
            </li>
            <li>
              <a
                className={styles.navigationLink}
                href="https://community.developer.atlassian.com/c/trello/42"
                target="_blank"
                rel="noopener noreferrer"
              >
                {format('developer-community-forums')}
                <ExternalLinkIcon />
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export const PowerUpPageNavigation: FunctionComponent<{
  idPlugin: string;
  subsection?: string | null;
}> = ({ idPlugin, subsection }) => {
  const { data, loading } = usePowerUpPageNavigationQuery({
    variables: {
      idPlugin,
    },
    waitOn: ['None'],
  });

  if (loading) {
    return null;
  }

  const plugin = data?.plugin ? data.plugin : null;

  if (!plugin) {
    navigate(`/power-ups/${idPlugin}`, { trigger: true });
    return null;
  }

  const isPublic = plugin?.public ? true : false;

  const defaultIcon = plugin?.iframeConnectorUrl
    ? defaultPowerUpIcon
    : defaultAppIcon;
  const powerUpIconUrl = plugin?.icon?.url || defaultIcon;

  let name = plugin.name || format('untitled');

  if (plugin.listings?.length) {
    let listing = plugin.listings.find((l) => l.locale === currentLocale);
    if (!listing) {
      listing = plugin.listings[0];
    }
    name = listing.name;
  }

  return (
    <div className={styles.sidebar}>
      <div className={styles.navigation}>
        <div className={styles.navigationSection}>
          <ul>
            <li>
              <RouterLink
                className={styles.navigationLink}
                href="/power-ups/admin"
              >
                <BackIcon dangerous_className={styles.backIcon} />
                {format('back')}
              </RouterLink>
            </li>
          </ul>
        </div>
        <div className={styles.navigationSection}>
          <div className={styles.powerUpHeader}>
            <span
              className={styles.powerUpIcon}
              style={{
                backgroundImage: `url(${powerUpIconUrl})`,
              }}
            />
            <span className={styles.powerUpName}>{name}</span>
          </div>
          <ul className={styles.powerUpSettings}>
            <li>
              <RouterLink
                className={classNames({
                  [styles.navigationLink]: true,
                  [styles.isActive]: typeof subsection === 'undefined',
                })}
                href={`/power-ups/${idPlugin}/edit`}
              >
                {format('basic-information')}
              </RouterLink>
            </li>
            <li>
              <RouterLink
                className={classNames({
                  [styles.navigationLink]: true,
                  [styles.isActive]: subsection === 'api-key',
                })}
                href={`/power-ups/${idPlugin}/edit/api-key`}
              >
                {format('api-key')}
              </RouterLink>
            </li>
            <li>
              <RouterLink
                className={classNames({
                  [styles.navigationLink]: true,
                  [styles.isActive]: subsection === 'capabilities',
                })}
                href={`/power-ups/${idPlugin}/edit/capabilities`}
              >
                {format('capabilities')}
              </RouterLink>
            </li>
            <li>
              <RouterLink
                className={classNames({
                  [styles.navigationLink]: true,
                  [styles.isActive]: subsection === 'listings',
                })}
                href={`/power-ups/${idPlugin}/edit/listings`}
              >
                {format('listings')}
              </RouterLink>
            </li>
            <li>
              <RouterLink
                className={classNames({
                  [styles.navigationLink]: true,
                  [styles.isActive]: subsection === 'collaborators',
                })}
                href={`/power-ups/${idPlugin}/edit/collaborators`}
              >
                {format('collaborators')}
              </RouterLink>
            </li>
            <li>
              <RouterLink
                className={classNames({
                  [styles.navigationLink]: true,
                  [styles.isActive]: subsection === 'metrics',
                })}
                href={`/power-ups/${idPlugin}/edit/metrics`}
              >
                {format('metrics')}
              </RouterLink>
            </li>
            <li>
              <RouterLink
                className={classNames({
                  [styles.navigationLink]: true,
                  [styles.isActive]: subsection === 'privacy',
                })}
                href={`/power-ups/${idPlugin}/edit/privacy`}
              >
                {format('privacy-and-compliance')}
              </RouterLink>
            </li>
            {isPublic && (
              <li>
                <RouterLink
                  className={classNames({
                    [styles.navigationLink]: true,
                    [styles.isActive]: subsection === 'distribution',
                  })}
                  href={`/power-ups/${idPlugin}/edit/distribution`}
                >
                  {format('distribution')}
                </RouterLink>
              </li>
            )}
            <li>
              <RouterLink
                className={classNames({
                  [styles.navigationLink]: true,
                  [styles.isActive]: subsection === 'delete',
                })}
                href={`/power-ups/${idPlugin}/edit/delete`}
              >
                {format('delete-power-up')}
              </RouterLink>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};
